// @greenlight-spec: 59-platform-agent.md#dashboard-ia
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { AgentPulse, LiveStepLine, useCycle } from '@/components/agents/Transcript';
import { MetricSparkline } from '@/components/greenlight/MetricSparkline';
import { cn } from '@/lib/utils';

import { ACTION_REQUESTS, ACTIVITY_7D, type SessionDef } from './fixtures';
import { AgentTile, RoadmapTag, agentById, runningSessions, usd } from './shared';

/** Re-renders every `everyMs` so elapsed times and token counters tick. */
export function useNow(everyMs = 1000): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

export function elapsed(iso: string, now: number): string {
  const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** A running session's token count, nudged forward each second so the fixture reads as live. */
export function liveTokens(session: SessionDef, now: number): string {
  const s = Math.max(0, (now - new Date(session.startedAt).getTime()) / 1000);
  const n = session.tokens + Math.floor((s % 600) * 140);
  return `${(n / 1000).toFixed(1)}k`;
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const eyebrow = 'font-mono text-[10.5px] uppercase tracking-[0.06em]';

/**
 * The top of Greenlight Agents: what is running this second, and the week in
 * numbers. Every running session is a row you can open.
 */
export function LiveActivity({
  onWaitingClick,
}: {
  onWaitingClick: () => void;
}): React.JSX.Element {
  const now = useNow();
  const running = runningSessions();
  const waiting = ACTION_REQUESTS.filter((r) => r.status === 'pending').length;
  const today = ACTIVITY_7D.sessions[ACTIVITY_7D.sessions.length - 1] ?? 0;
  const yesterday = ACTIVITY_7D.sessions[ACTIVITY_7D.sessions.length - 2] ?? 0;

  return (
    <section
      className="overflow-hidden rounded-gl-lg border border-border bg-card"
      aria-label="Live activity"
    >
      <div className="grid grid-cols-2 gap-px bg-border md:grid-cols-4">
        <Cell label="Running now">
          <span className="flex items-center gap-2.5">
            <Value>{running.length}</Value>
            <AgentPulse className="size-2.5 [&>span]:size-2.5" />
          </span>
          <Sub>across {new Set(running.map((s) => s.agentId)).size} agents</Sub>
        </Cell>
        <Cell label="Sessions today">
          <Value>{today}</Value>
          <Sub>
            {today >= yesterday ? '+' : '−'}
            {Math.abs(today - yesterday)} vs yesterday
          </Sub>
        </Cell>
        <Cell label="Waiting on you">
          <button
            type="button"
            onClick={onWaitingClick}
            className="group flex items-center gap-2.5 text-left"
          >
            <Value>{waiting}</Value>
            {waiting > 0 && <span className="size-2 rounded-full bg-gl-amber" aria-hidden />}
          </button>
          <button
            type="button"
            onClick={onWaitingClick}
            className="inline-flex items-center gap-1 text-[12px] text-link hover:underline"
          >
            Review requests <ArrowRight className="size-3" aria-hidden />
          </button>
        </Cell>
        <Cell label="Spend, 7 days">
          <Value>{usd(sum(ACTIVITY_7D.spend))}</Value>
          <MetricSparkline
            data={ACTIVITY_7D.spend}
            pointLabels={ACTIVITY_7D.days}
            formatValue={(v) => usd(v)}
            className="mt-1 h-8"
            ariaLabel="Spend per day, last 7 days"
          />
        </Cell>
      </div>

      {running.length > 0 && (
        <ul className="divide-y divide-border border-t border-border">
          {running.map((s) => (
            <RunningRow key={s.id} session={s} now={now} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RunningRow({ session, now }: { session: SessionDef; now: number }) {
  const agent = agentById(session.agentId);
  const step = useCycle(session.liveSteps ?? [session.summary], 3200 + session.id.length * 90);
  return (
    <li>
      <Link
        to={`/agents/${session.agentId}/sessions/${session.id}`}
        className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-4 py-2.5 hover:bg-accent/60 md:grid-cols-[auto_minmax(0,240px)_minmax(0,1fr)_auto]"
      >
        <AgentTile agentId={session.agentId} size="sm" />
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-semibold">{session.title}</span>
          <span className="flex min-w-0 items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <span className="truncate">{agent?.name}</span>
            {agent?.status === 'roadmap' && <RoadmapTag className="px-1.5 text-[10px]" />}
          </span>
        </span>
        <LiveStepLine
          text={step ?? ''}
          className="col-span-3 col-start-1 row-start-2 md:col-span-1 md:col-start-auto md:row-start-auto"
        />
        <span className="flex items-center gap-3 justify-self-end font-mono text-[11.5px] tabular-nums text-muted-foreground md:row-start-auto">
          <span title="Elapsed">{elapsed(session.startedAt, now)}</span>
          <span className="hidden sm:inline" title="Tokens so far">
            {liveTokens(session, now)} tok
          </span>
          <ArrowRight className="size-3.5" aria-hidden />
        </span>
      </Link>
    </li>
  );
}

function Cell({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1 bg-card px-4 py-3.5', className)}>
      <span className={eyebrow}>{label}</span>
      {children}
    </div>
  );
}

const Value = ({ children }: { children: React.ReactNode }) => (
  <span className="font-display text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
    {children}
  </span>
);
const Sub = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[12px] text-muted-foreground">{children}</span>
);
